import axios from "axios";

const responseDetail = (data: unknown) => typeof data === "string" ? data
  : data == null ? "" : JSON.stringify(data, null, 2);

// A save failure used to render as one flat pre-formatted blob - a short, actionable sentence
// buried in the same monospace block as the timestamp, request line, and raw server payload, with
// no visual distinction between "what you should do" and "what to paste into a support ticket".
// Splitting it into `headline` (shown prominently) and `details` (technical, secondary) lets a
// dialog present them differently; `full` is both concatenated, unchanged from what this function
// used to return directly, for copy-to-clipboard and any caller that just wants the whole thing.
export type QuoteSaveError = {
  headline: string;
  details: string;
  full: string;
  // Only the 400-499 "the server rejected a field" case is worth opening by default - the
  // validation detail in `details` is the actual fix, not just reference material for support.
  detailsExpandedByDefault: boolean;
};

const build = (headline: string, details: string, detailsExpandedByDefault = false): QuoteSaveError =>
  ({ headline, details, full: `${headline}\n${details}`, detailsExpandedByDefault });

export const quoteSaveErrorMessage = (error: unknown, operation: "create" | "update", url: string, now = new Date()): QuoteSaveError => {
  const context = `Time: ${now.toISOString()}\nRequest: ${operation.toUpperCase()} ${url}`;
  if (!axios.isAxiosError(error)) {
    const detail = error instanceof Error ? error.message : String(error);
    return build("Quote save failed. Your edits are still available in this browser - try saving again in a moment.", `${context}\n${detail}`);
  }

  const status = error.response?.status;
  const detail = responseDetail(error.response?.data) || error.message;
  if (status === 401) return build(
    "Your session expired before the quote could be saved. Your edits are still available - sign in again, restore the draft if prompted, then retry.",
    `${context}\nHTTP 401${detail ? `\n${detail}` : ""}`);

  const timedOut = error.code === "ECONNABORTED" || error.code === "ETIMEDOUT" || /timeout/i.test(error.message);
  if (!error.response || timedOut) return build(
    "The save request did not get a response, so it's unknown whether it went through. Check your connection, then check the Quotes list before retrying so you don't create a duplicate. Your edits are still available in this browser.",
    `${context}\n${error.code || "NETWORK_ERROR"}: ${error.message}`);

  if (status && status >= 400 && status < 500) return build(
    "The quote was not saved because the server rejected one or more fields. Review the detail below, correct it, and retry.",
    `${context}\nHTTP ${status}${detail ? `\n${detail}` : ""}`, true);

  return build(
    "The quote could not be saved because the server returned an error. Your edits are still available in this browser - try again in a few minutes, and share the detail below with support if it keeps happening.",
    `${context}\nHTTP ${status || "unknown"}${detail ? `\n${detail}` : ""}`);
};
